import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Legal.css";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO 
        title="Terms & Conditions"
        description="IntentLogic Terms and Conditions - Read our terms of service, usage requirements, and legal agreements for using our website and services."
        keywords="terms and conditions, terms of service, legal agreement, usage policy, website terms, service agreement"
        canonical="/terms"
      />
      <main className="legal-content">
        <div className="legal-container">
          <span className="legal-label">INTENTLOGIC</span>
          <h1>Terms & Conditions</h1>
          <p className="legal-updated">Last Updated: December 2025</p>
          
          <div className="legal-text">
            <p>
              Thank you for visiting the IntentLogic website located at www.intentlogic.com (the "Site"). 
              The Site is an Internet property of IntentLogic ("IntentLogic," "we," "our" or "us"). We require 
              that each visitor to the Site ("user," "you" or "your") agrees on your own behalf and on behalf 
              of any corporation or other legal entity that you represent or for which you access the Site 
              (your "Company") to the following terms and conditions of use ("Terms") in their entirety.
            </p>
            
            <p>
              The Site is available only to individuals that: (a) are at least eighteen (18) years of age 
              (or the applicable age of majority, if greater than eighteen (18) years of age in their 
              jurisdiction of residence); and (b) can enter into legally binding contracts under applicable 
              law (collectively, "Usage Requirements").
            </p>
            
            <p>
              These Terms constitute the entire and only agreement between you and IntentLogic with respect 
              to your use of the IntentLogic Offerings and are inclusive of the IntentLogic Privacy Policy 
              ("Privacy Policy"). We may change these Terms at any time, and all such changes are effective 
              immediately upon notice. Your use of the Site constitutes your continuing agreement to be bound 
              by these Terms, as they are amended from time to time. <strong>If you do not agree to these 
              Terms, you are not authorized to use the IntentLogic Offerings in any manner or form.</strong>
            </p>
            
            <h2>1. IntentLogic Offerings</h2>
            <p>
              The Site's purpose is to provide general information about IntentLogic, our management team 
              and the services we provide to advertisers and publishers.
            </p>
            <p>
              <strong>(a) Content.</strong> The Site contains Content which includes, but is not limited to: 
              (i) sections that describe IntentLogic's business; (ii) information regarding IntentLogic's 
              management team; (iii) blog posts regarding the latest IntentLogic news; and (iv) other 
              materials regarding IntentLogic and our business. <strong>The Content is offered for 
              informational purposes only.</strong>
            </p>
            <p>
              The Site and Content may include hyperlinks to other websites which are not maintained by 
              IntentLogic. We are not responsible for the content of such external websites and we make 
              no representations whatsoever concerning the content or accuracy of, opinions expressed in, 
              or other links provided by such websites.
            </p>
            <p>
              <strong>(b) Social Media Pages.</strong> The Site contains links to the various IntentLogic 
              Social Media Pages. The Social Media Pages are hosted and made available on third-party 
              websites by third-party entities. Your use of Social Media Pages and Social Media Websites 
              shall be governed by those Social Media Websites' applicable agreements, terms and conditions.
            </p>
            <p>
              <strong>(c) Contact Services.</strong> The Site provides you with the means to contact us 
              regarding information about IntentLogic and our services. Upon entering your Contact Data 
              and clicking on the applicable submission button on the Site, you may be contacted by an 
              IntentLogic representative regarding your request. If you provide us with your telephone 
              number, you consent to us calling you on the telephone number provided.
            </p>
            
            <h2>2. Privacy Policy</h2>
            <p>
              Information that you provide to us or that we collect about you and your Company through 
              your access to and use of the IntentLogic Offerings is subject to our Privacy Policy, the 
              terms of which are hereby incorporated by reference into these Terms. We encourage you to 
              read and become familiar with our Privacy Policy. To view our Privacy Policy, 
              <Link to="/privacy-policy"> Click Here</Link>.
            </p>
            
            <h2>3. Intellectual Property</h2>
            <p>
              The content, organization, graphics, design, compilation, services and other matters related 
              to the IntentLogic Offerings are protected under applicable copyrights, trademarks and other 
              proprietary (including, but not limited to, intellectual property) rights. The copying, 
              redistribution, publication or sale by you of any part of the IntentLogic Offerings is 
              strictly prohibited.
            </p>
            
            <h2>4. License Grant</h2>
            <p>
              As a user of the Site, you are granted a non-exclusive, non-transferable, revocable and 
              limited license to access and use the IntentLogic Offerings and associated content in 
              accordance with these Terms. We may terminate this license at any time for any reason. 
              You may use the IntentLogic Offerings for your own personal, non-commercial use.
            </p>
            <p>
              No part of the IntentLogic Offerings may be reproduced in any form or incorporated into 
              any information retrieval system, electronic or mechanical. You may not use, copy, emulate, 
              clone, rent, lease, sell, modify, decompile, disassemble, reverse engineer or transfer the 
              IntentLogic Offerings or any portion thereof.
            </p>
            
            <h2>5. Editing, Deleting and Modification</h2>
            <p>
              We reserve the right in our sole discretion to edit and/or delete any documents, information 
              or other content appearing on the Site.
            </p>
            
            <h2>6. Security</h2>
            <p>
              IntentLogic takes such commercially reasonable measures as it deems appropriate to secure 
              and protect information transmitted to and from the Site. Nevertheless, we cannot and do 
              not guarantee that any such transmissions are or will be totally secure. You are responsible 
              for maintaining the confidentiality of any information about you and/or your Company used 
              in connection with your use of the Site.
            </p>
            
            <h2>7. Disclaimer</h2>
            <p>
              <strong>THE USE OF THE INTENTLOGIC OFFERINGS BY YOU AND YOUR COMPANY IS AT YOUR AND ITS 
              SOLE RISK. ACCORDINGLY, THE SITE AND THE INTENTLOGIC OFFERINGS ARE PROVIDED "AS IS" AND 
              "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING 
              WITHOUT LIMITATION ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
              PURPOSE, TITLE, AND NON-INFRINGEMENT, AND ALL SUCH WARRANTIES ARE EXPRESSLY DISCLAIMED 
              AND EXCLUDED, TO THE FULLEST EXTENT PERMITTED BY LAW.</strong>
            </p>
            
            <h2>8. Limitation of Liability</h2>
            <p>
              <strong>YOU EXPRESSLY UNDERSTAND AND AGREE THAT WE SHALL NOT BE LIABLE TO YOU, YOUR COMPANY 
              OR ANY THIRD-PARTY FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL AND/OR 
              EXEMPLARY DAMAGES INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, GOODWILL, 
              USE, DATA OR OTHER INTANGIBLE LOSSES (EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF 
              SUCH DAMAGES), TO THE FULLEST EXTENT PERMISSIBLE BY LAW FOR YOUR USE OR YOUR INABILITY TO 
              USE THE INTENTLOGIC OFFERINGS.</strong>
            </p>
            <p>
              IF APPLICABLE LAW DOES NOT PERMIT SUCH LIMITATIONS, OUR MAXIMUM LIABILITY TO YOU UNDER ANY 
              AND ALL CIRCUMSTANCES WILL BE ONE HUNDRED DOLLARS ($100.00) IN ANY CALENDAR YEAR.
            </p>
            
            <h2>9. Indemnification</h2>
            <p>
              You agree to defend and indemnify IntentLogic, and our officers, directors, employees, 
              representatives, and agents, against all claims, demands, suits, or other proceedings, 
              and all resulting loss, damage, liability, cost, and expense (including reasonable 
              attorneys' fees), made by any third party arising out of: (a) information that you provide 
              through the Site; (b) your access to and use of the IntentLogic Offerings; (c) your 
              violation of these Terms; and (d) your violation of any rights of any third party.
            </p>
            
            <h2>10. Jurisdiction</h2>
            <p>
              These Terms will be construed and enforced in accordance with the laws of the Commonwealth 
              of Puerto Rico. Each of you and your Company submits to personal jurisdiction in Puerto Rico, 
              and any cause of action arising under these Terms or otherwise involving this Site will be 
              brought exclusively in a court in Puerto Rico.
            </p>
            
            <h2>11. Waiver of Jury Trial</h2>
            <p>
              <strong>YOU HEREBY KNOWINGLY AND VOLUNTARILY WAIVE THE RIGHT TO A JURY TRIAL IN ANY ACTIONS, 
              SUITS, OR PROCEEDINGS ARISING OUT OF OR RELATING TO THESE TERMS AND THE MATTERS CONTEMPLATED 
              HEREBY.</strong>
            </p>
            
            <h2>12. Miscellaneous & Contact Us</h2>
            <p>
              Should any part of these Terms be held invalid or unenforceable, that portion shall be 
              construed consistent with applicable law and the remaining portions shall remain in full 
              force and effect. Our failure to enforce any provision of the Terms shall not be deemed a 
              waiver of such provision nor of the right to enforce such provision.
            </p>
            
            <p>If you have any questions regarding the Terms, or would like more information from IntentLogic, please contact us:</p>
            
            <div className="legal-contact">
              <p><strong>IntentLogic</strong></p>
              <p>Metro Office Park</p>
              <p>14 Calle 2 102</p>
              <p>Guaynabo, Puerto Rico 00968</p>
              <p>Telephone: 855-948-4423</p>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default TermsConditions;
